import { Link } from 'react-router-dom'

import type { Subscription } from './types'
import { formatDate } from './types'
import { Banner, buttonClasses } from '../../ui'

/**
 * El aviso de estado de pago.
 *
 * Existe porque el motor de cobro NO corta el servicio al primer rechazo
 * (PLAN.md §12.1): hay hasta 7 días de reintentos durante los cuales todo
 * sigue funcionando. Eso sólo sirve de algo si el estudio se entera — un
 * periodo de gracia silencioso termina en corte por sorpresa, que es
 * exactamente el resultado que la gracia pretendía evitar.
 *
 * El tono sube con la urgencia, y nunca da miedo de más: mientras se pueda
 * cobrar, el mensaje es "actualiza la tarjeta", no "vas a perder tus fotos".
 * Los archivos no se borran nunca por impago.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `status` elige el RELATO; `uploads_allowed` decide lo que se AFIRMA
 * ─────────────────────────────────────────────────────────────────────────────
 * Cada estado necesita su propio texto y su propio tono, así que el `switch`
 * sobre `status` se queda. Lo que no puede deducirse aquí es si el estudio
 * puede subir: eso es una regla de negocio que vive en
 * `SubscriptionStatus::allowsUploads()` y que el servidor ya envía resuelta.
 *
 * Antes esta pantalla la reimplementaba de hecho —decía «no puedes subir» en
 * unos estados y no en otros—, y las dos versiones coincidían por casualidad.
 * El día que en el backend la gracia dejara de permitir subir, aquí seguiría
 * poniendo «lo reintentaremos» mientras la subida devuelve 403.
 */
export function SubscriptionStatusBanner({ subscription }: { subscription: Subscription }) {
  const {
    status,
    cancel_at_period_end,
    grace_until,
    current_period_end,
    uploads_allowed,
    billing_method,
    attempts_left,
  } = subscription

  if (status === 'active' && !cancel_at_period_end) return null

  // Lo que se afirma sobre las subidas sale del servidor, no del estado.
  const sobreLasSubidas = uploads_allowed ? '' : 'No puedes subir archivos nuevos. '

  /*
   | El método decide qué se le PIDE al estudio, y equivocarse no es un matiz de
   | redacción (PLAN.md §12.8): decirle «actualiza tu tarjeta» a quien nunca dio
   | una le pide una acción que no puede hacer, y de paso le enseña que estos
   | avisos no son de fiar — justo antes del que anuncia el corte.
   */
  const porTransferencia = billing_method === 'transfer'

  const notice = (() => {
    switch (status) {
      case 'past_due':
      case 'grace':
        return porTransferencia
          ? {
              tone: 'warning' as const,
              title: 'No nos consta tu transferencia',
              body: `Si ya la hiciste, avísanos con el número de operación y la comprobamos hoy. Tienes hasta el ${formatDate(grace_until)} antes de que se interrumpa el servicio.`,
              action: { to: '/activar', label: 'Avisar de mi transferencia' },
            }
          : {
              /*
               | El tono sube con `attempts_left`, que es lo que faltaba para
               | cumplir lo que promete la cabecera de este fichero.
               |
               | Antes decía LO MISMO en el primer rechazo y en el último: «lo
               | reintentaremos automáticamente». Al tercero eso es falso a
               | medias —queda uno— y al cuarto ya no hay reintento: la
               | suscripción expira. Un aviso que no cambia cuando la situación
               | cambia enseña a no leerlo, justo antes del que había que leer.
               |
               | El número NO se calcula aquí. Sale de `RetrySchedule` en el
               | servidor, por lo mismo que `uploads_allowed`: un «de 3» escrito
               | en TypeScript deja de coincidir el día que el calendario
               | cambie, y nadie se entera hasta que alguien pierde el servicio
               | antes de lo que le dijimos.
               */
              tone: attempts_left <= 1 ? ('danger' as const) : ('warning' as const),
              title:
                attempts_left === 1 ? 'Último intento de cobro' : 'No pudimos cobrar tu suscripción',
              body:
                attempts_left === 0
                  ? `Ya no quedan reintentos. Actualiza tu tarjeta antes del ${formatDate(grace_until)} o el servicio se interrumpe. Tus galerías siguen publicadas y tus clientes pueden seguir descargando.`
                  : `${attempts_left === 1 ? 'Nos queda un intento' : `Nos quedan ${attempts_left} intentos`}. Actualiza tu tarjeta antes del ${formatDate(grace_until)} para que no se interrumpa el servicio.`,
            }
      case 'expired':
      case 'canceled':
        return {
          tone: 'danger' as const,
          title: 'Tu suscripción está inactiva',
          body: `${sobreLasSubidas}Tus galerías siguen publicadas y tus clientes pueden seguir descargando con normalidad.`,
        }
      case 'trialing':
        return {
          tone: 'warning' as const,
          title: 'Falta el primer pago',
          body: `Tu cuenta existe${uploads_allowed ? '' : ' pero todavía no puedes subir archivos'}. ${
            porTransferencia
              ? 'En cuanto confirmemos tu transferencia, se activa.'
              : 'Elige cómo pagar para activarla.'
          }`,
          action: {
            to: '/activar',
            label: porTransferencia ? 'Ver mi transferencia' : 'Pagar y activar',
          },
        }
      default:
        return {
          tone: 'neutral' as const,
          title: 'Tu suscripción no se renovará',
          body: `Seguirás teniendo todo hasta el ${formatDate(current_period_end)}. Puedes reactivarla antes de esa fecha.`,
        }
    }
  })()

  return (
    <Banner
      tone={notice.tone}
      title={notice.title}
      action={
        'action' in notice && notice.action ? (
          <Link className={buttonClasses('primary', 'sm')} to={notice.action.to}>
            {notice.action.label}
          </Link>
        ) : undefined
      }
    >
      {notice.body}
    </Banner>
  )
}
